import { useEffect, useState } from "react";
import PropTypes from 'prop-types';

// @mui material components
import Grid from "@mui/material/Grid";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftComponents/SoftBox";
import SoftTypography from "components/SoftComponents/SoftTypography";

// Soft UI Dashboard PRO React example components
import ClearwaysGroupCard from "components/Groups/ClearwaysGroupCard";

import { useQuery } from '@tanstack/react-query';
import { useAuth0 } from "@auth0/auth0-react";

// API/MODELS
import groupAPI from "services/api/groups";
import bksAPI from "services/api/bks";
import userAPI from "services/api/user";

// Images
import { processGroupData } from "utils/group";

import { disableMap } from "utils/map";
import { useSoftUIController } from "hooks/context";
import { wrapper, loadingScreen } from "utils/wrapper";
import { Box, Card } from "@mui/material";

function ClearwayDeviceGroupsView({ map, user }) {
  const [controller] = useSoftUIController();
  const { deviceLayout, selectedOrg, userPermissions, appearance } = controller;

  useEffect(() => {
    if (!map.current) return;
    disableMap(map, user, selectedOrg);
  }, [selectedOrg]);

  groupAPI.loadToken(useAuth0());
  bksAPI.loadToken(useAuth0());

  const showAdvancedDeviceLevel = userPermissions.indexOf('devices-advanced:view') >= 0;

  const groups = useQuery({
    queryKey: ['getAllGroups', selectedOrg],
    queryFn: async () => {
      return await groupAPI.getAllGroups();
    }
  });

  const devices = useQuery({
    queryKey: ['getAllDevices', selectedOrg],
    queryFn: async () => await bksAPI.getAllDevices(),
    refetchInterval: 300 * 1000,
    refetchIntervalInBackground: true,
  });

  if (groups.isLoading || devices.isLoading) {
    return loadingScreen(controller, true);
  }

  if (groups.error || devices.error) {
    return wrapper("An error has occurred", controller);
  }

  if (!groups.data || !devices.data) {
    return loadingScreen(controller, false);
  }

  var groups_data = groups.data.filter((row) => row.type == "clearways").map(function (group) {
    var ret = processGroupData(groups.dataUpdatedAt, group, devices.data, showAdvancedDeviceLevel);
    return ret;
  }).filter((row) => { return typeof row.parent_id == 'undefined' });

  return wrapper((
    <SoftBox borderRadius="20px" color={"white"} width="100%" height={deviceLayout == 'mobile' ? 'calc(100svh - 4.25rem - 8px)' : "calc(100svh - 5.5rem - 16px)"} minHeight={deviceLayout == 'mobile' && "calc(100svh - 4.25rem - 8px)"}
      sx={({ palette, functions: { rgba } }) => ({ backgroundColor: rgba(palette[appearance].background, 0.7), overflowY: 'hidden' })}
    >
      <SoftBox mt={1} display="flex" sx={{ height: `calc(100% - 16px)` }}>
        <SoftBox py={0} px={1} width={"calc(100% - 16px)"} height="auto">
          <Card sx={({ palette, functions: { rgba } }) => ({ backgroundColor: rgba(palette[appearance].content, 1), height: '100%' })}>
            <SoftBox py={1} px={deviceLayout == 'mobile' ? 1 : 2} sx={({ palette, functions: { rgba } }) => ({ borderRadius: '16px 16px 0 0', backgroundColor: rgba(palette[appearance].header, 1), borderBottom: `1px solid ${rgba(palette[appearance].border, 1)}` })}>
              <SoftTypography variant={deviceLayout == "mobile" ? "h5" : "h4"} fontWeight="bold" sx={{ mr: 0.5, flexShrink: 1, width: 'fit-content' }}>
                Clearways Groups
              </SoftTypography>
            </SoftBox>
            <SoftBox p={1} sx={{ overflowY: 'auto' }}>
              <Grid container spacing={3}>
                {groups_data.map((group) => (
                  <Grid item xxs={12} md={6} lg={4} x3l={3} key={group.group_id} onClick={() => { group.group_id }}>
                    <ClearwaysGroupCard group={group} />
                  </Grid>
                ))}
              </Grid>
            </SoftBox>
          </Card>
        </SoftBox>
      </SoftBox>
    </SoftBox>
  ), controller);
}

ClearwayDeviceGroupsView.propTypes = {
  map: PropTypes.object,
  user: PropTypes.object,
}

export default ClearwayDeviceGroupsView;
